import { FerryIngredient } from '@apps/main/modules/recipe/ingredients';
import { LabelFormatterType } from '@apps/main/new-desktop/core/components/formatters';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { FerryFilters } from '@apps/main/new-desktop/modules/ferryFilters';
import { FerryFilterType } from '@apps/main/new-desktop/modules/ferryFilters';

import { createIngredientId } from '@libs/trip-planner-interfaces';
import type {
  CarrierName,
  StringRangeType,
} from '@libs/trip-planner-interfaces';
import { assertValue, HHMMToMiliseconds, milisecondsToHHMM } from '@libs/utils';

import type {
  CheckboxFilterData,
  RangeSliderFilterData,
} from '../../../modules/filters';
import { FilterType } from '../../../modules/filters';

const { t } = i18n.global;

const MAX_RANGE_TIME_IN_MS = 86340000;
const HALF_HOUR_IN_MS = 1800000;

export function getDepartureTimeRangeSliderFilter(
  ferry: FerryIngredient
): RangeSliderFilterData {
  const initialFilterValue = [
    ferry.filters.departureTimeFilters?.from
      ? HHMMToMiliseconds(ferry.filters.departureTimeFilters?.from)
      : 0,
    ferry.filters.departureTimeFilters?.to
      ? HHMMToMiliseconds(ferry.filters.departureTimeFilters.to)
      : MAX_RANGE_TIME_IN_MS,
  ];
  return {
    filterType: FilterType.RangeSlider,
    options: {
      id: ferry.ingredientId,
      initialValue: initialFilterValue,
      min: 0,
      max: MAX_RANGE_TIME_IN_MS,
      step: HALF_HOUR_IN_MS,
    },
    title: `${ferry.fromPortText} - ${ferry.toPortText}`,
    labelFormatter: {
      formatterType: LabelFormatterType.TimeRangeFormatter,
      data: {
        value: initialFilterValue,
        color: 'color-neutral-50',
        font: 'font-label-m-light',
      },
    },
  };
}

export function getArrivalTimeRangeSliderFilter(
  ferry: FerryIngredient
): RangeSliderFilterData {
  const initialFilterValue = [
    ferry.filters.arrivalTimeFilters?.from
      ? HHMMToMiliseconds(ferry.filters.arrivalTimeFilters?.from)
      : 0,
    ferry.filters.arrivalTimeFilters?.to
      ? HHMMToMiliseconds(ferry.filters.arrivalTimeFilters.to)
      : MAX_RANGE_TIME_IN_MS,
  ];
  return {
    filterType: FilterType.RangeSlider,
    options: {
      id: ferry.ingredientId,
      initialValue: initialFilterValue,
      min: 0,
      max: MAX_RANGE_TIME_IN_MS,
      step: HALF_HOUR_IN_MS,
    },
    title: `${ferry.fromPortText} - ${ferry.toPortText}`,
    labelFormatter: {
      formatterType: LabelFormatterType.TimeRangeFormatter,
      data: {
        value: initialFilterValue,
        color: 'color-neutral-50',
        font: 'font-label-m-light',
      },
    },
  };
}

export function getDefaultFilters(): FerryFilters {
  return {
    ingredientId: createIngredientId(''),
    arrivalTime: {
      filterType: FerryFilterType.ArrivalTime,
      label: t('filter.flightFilters.arrival'),
      filters: [],
    },

    departureTime: {
      filterType: FerryFilterType.DepartureTime,
      filters: [],
      label: t('filter.flightFilters.departure'),
    },
    carriers: {
      filterType: FerryFilterType.Carriers,
      data: [],
      label: t('filter.ferryFilters.carrier'),
    },
  };
}

export function getStringRangeType(range: number[]): StringRangeType {
  const firstValue = range[0];
  const secondValue = range[1];

  assertValue(
    firstValue,
    'flightsListFiltersStoreLogic.ts > getStringRangeType > firstValue'
  );
  assertValue(
    secondValue,
    'flightsListFiltersStoreLogic.ts > getStringRangeType > secondValue'
  );
  return {
    from: firstValue === 0 ? undefined : milisecondsToHHMM(firstValue),
    to:
      secondValue === MAX_RANGE_TIME_IN_MS
        ? undefined
        : milisecondsToHHMM(secondValue),
  };
}

export function getCarriersCheckboxFilter(
  carriers: CarrierName[],
  ferry: FerryIngredient
): CheckboxFilterData[] {
  return carriers.map((carrier) => ({
    filterType: FilterType.Checkbox,
    ingredientId: ferry.ingredientId,
    options: {
      checked: ferry.filters.carrierFilters.includes(carrier.code),
      label: carrier.name,
      code: carrier.code,
      type: {
        theme: 'grey',
        size: 'small',
        isDisabled: false,
      },
    },
  }));
}

export function getFilterKeyFromTagId(tagId: string): string | undefined {
  if (Object.keys(FerryIngredient.defaultFilters).includes(tagId)) {
    return tagId;
  }

  const filterKey = tagId.split('.')[0];

  if (
    filterKey &&
    Object.keys(FerryIngredient.defaultFilters).includes(filterKey)
  ) {
    return filterKey;
  }

  return undefined;
}
